/**
 * Content registry — auto-discovers markdown files from docs/ via Vite glob.
 *
 * To add a new doc, runbook, or learning module, just drop a .md file into:
 *   docs/documentation/  or  docs/runbooks/  or  docs/learning/<category>/
 *
 * The slug is derived from the filename (e.g. getting-started.md → "getting-started").
 * The title is extracted from the first H1 heading in the file.
 */

// Vite glob import — loads .md files as raw strings
const docModules = import.meta.glob('/docs/documentation/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>
const runbookModules = import.meta.glob('/docs/runbooks/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>
const learningDbtModules = import.meta.glob('/docs/learning/dbt/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>
const learningDatabricksModules = import.meta.glob(
  '/docs/learning/databricks/*.md',
  { query: '?raw', import: 'default', eager: true }
) as Record<string, string>

export interface ContentEntry {
  slug: string
  title: string
  content: string
  path: string
  description: string
}

export type LearningCategory = 'dbt' | 'databricks'

export interface LearningEntry extends ContentEntry {
  category: LearningCategory
}

// Strip inline markdown formatting (bold/italic/code/links) so descriptions
// used in card previews read as plain text.
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
}

// Extract the first prose paragraph after the H1 title (skipping headings,
// lists, code fences, and blank lines) to use as a short card description.
function extractDescription(content: string): string {
  const lines = content.split('\n')
  for (const line of lines) {
    const trimmed = line.trim()
    if (
      !trimmed ||
      trimmed.startsWith('#') ||
      trimmed.startsWith('-') ||
      trimmed.startsWith('*') ||
      trimmed.startsWith('```') ||
      trimmed.startsWith('|') ||
      /^\d+\.\s/.test(trimmed)
    ) {
      continue
    }
    return stripInlineMarkdown(trimmed)
  }
  return ''
}

function parseEntries(
  modules: Record<string, string>,
  basePath: string
): ContentEntry[] {
  return Object.entries(modules).map(([filePath, content]) => {
    const filename = filePath.split('/').pop()?.replace('.md', '') ?? ''
    const slug = filename
    // Extract title from first H1
    const titleMatch = content.match(/^#\s+(.+)$/m)
    const title = titleMatch ? titleMatch[1] : slug.replace(/-/g, ' ')

    return {
      slug,
      title,
      content,
      path: `${basePath}/${slug}`,
      description: extractDescription(content),
    }
  })
}

function parseLearningEntries(
  modules: Record<string, string>,
  category: LearningCategory
): LearningEntry[] {
  return parseEntries(modules, '/learning').map((entry) => ({
    ...entry,
    category,
  }))
}

export const documentationEntries: ContentEntry[] = parseEntries(
  docModules,
  '/documentation'
)
export const runbookEntries: ContentEntry[] = parseEntries(
  runbookModules,
  '/runbooks'
)
export const learningEntries: LearningEntry[] = [
  ...parseLearningEntries(learningDbtModules, 'dbt'),
  ...parseLearningEntries(learningDatabricksModules, 'databricks'),
]

export function getDocBySlug(slug: string): ContentEntry | undefined {
  return documentationEntries.find((e) => e.slug === slug)
}

export function getRunbookBySlug(slug: string): ContentEntry | undefined {
  return runbookEntries.find((e) => e.slug === slug)
}

export function getLearningBySlug(slug: string): LearningEntry | undefined {
  return learningEntries.find((e) => e.slug === slug)
}
